import "./styles.scss";

import { useEffect, useRef, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import { useApp } from "context/app";

import useMenuSettings from "./hooks/useMenuSettings";
import { scrollToTop } from "libs/helpers/ScrollToTop";

import MobileBar from "components/MobileBar";
import TimeTracker from "components/TimeTracker";
import SendNotification from "components/SendNotification";
import BackMobile from "components/BackMobile";
import { CreateTask } from "./components";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";

import avatarImage from "assets/images/avatar-placeholder.png";

const mainClass = "menu-mobile";

const MenuMobile = () => {
  const { user, create_task_open, setCreateTaskOpen } = useApp();
  const navigate = useNavigate();
  const {
    dock,
    nav,
    submenu,
    isOpen,
    setIsOpen,
    setSubmenu,
    setSendNotificationOpen,
  } = useMenuSettings();
  const { t, i18n } = useTranslation();

  const mainRef = useRef(null);
  const buttonRefs = useRef([]);
  const [buttonMinWidths, setButtonMinWidths] = useState({});

  const measureRef = useRef(null);
  measureRef.current = () => {
    const widths = {};

    dock.forEach((item, index) => {
      if (!(item?.change || item?.replace)) return;

      const buttonEl = buttonRefs.current[index];

      if (!buttonEl) return;

      const buttonW = buttonEl.getBoundingClientRect().width;
      const iconW =
        buttonEl.querySelector("i")?.getBoundingClientRect().width ?? 0;

      const probe = document.createElement("span");
      probe.style.cssText =
        "position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;top:0;left:0;";
      buttonEl.appendChild(probe);

      probe.textContent = item.label;
      const defaultTextW = probe.getBoundingClientRect().width;

      probe.textContent = (item.change || item.replace).label;
      const altTextW = probe.getBoundingClientRect().width;

      buttonEl.removeChild(probe);

      const currentTextW = isOpen ? altTextW : defaultTextW;
      const padding = buttonW - Math.max(iconW, currentTextW);

      const defaultButtonW = Math.ceil(Math.max(iconW, defaultTextW) + padding);
      const altButtonW = Math.ceil(Math.max(iconW, altTextW) + padding);
      widths[index] = Math.max(defaultButtonW, altButtonW);
    });

    setButtonMinWidths(widths);
  };

  useEffect(() => {
    const measure = () => measureRef.current?.();
    let previousViewportWidth = window.innerWidth;

    if (document.fonts?.ready) {
      document.fonts.ready.then(measure);
    } else {
      measure();
    }

    let resizeTimer;
    const onResize = () => {
      const viewportWidth = window.innerWidth;

      if (viewportWidth === previousViewportWidth) return;

      previousViewportWidth = viewportWidth;
      clearTimeout(resizeTimer);
      setButtonMinWidths({});
      resizeTimer = setTimeout(measure, 100);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [i18n?.language]);

  useEffect(() => {
    scrollToTop(200, mainRef.current);
  }, [submenu]);

  return (
    <>
      <div className={mainClass}>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={mainRef}
              className={`${mainClass}__main`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "tween", duration: 0.2 }}
            >
              <MobileBar
                onWebsiteClick={() => {
                  setIsOpen(false);
                  setSubmenu(null);
                  setCreateTaskOpen(false);
                }}
              />
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={submenu?.label ?? "main"}
                  className={`${mainClass}__main-content`}
                  initial={{ x: !!submenu ? 32 : -32, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: !!submenu ? 32 : -32, opacity: 0 }}
                  transition={{ type: "tween", duration: 0.15 }}
                >
                  {!!user && !submenu && (
                    <div className={`${mainClass}__user`}>
                      <div className={`${mainClass}__user-row`}>
                        <div className={`${mainClass}__user-avatar`}>
                          <img src={user?.photo || avatarImage} alt="" />
                        </div>
                        <div className={`${mainClass}__user-body`}>
                          <p>
                            {t(
                              user?.type === "admin"
                                ? "Administrator"
                                : user?.type === "webmaster"
                                  ? "Webmaster"
                                  : "User",
                            )}{" "}
                            <strong>{`${user?.first_name} ${user?.last_name}`}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {!!submenu && (
                    <div className={`${mainClass}__back`}>
                      <BackMobile onClick={() => setSubmenu(null)} />
                    </div>
                  )}
                  <div className={`${mainClass}__nav`}>
                    {(submenu?.items ?? nav).map((item, index) => {
                      const Tag = item?.href ? NavLink : "button";
                      const isDefault = item?.type === "button";
                      const isActive = item?.active ?? false;

                      return (
                        <Tag
                          key={index}
                          className={classnames(`${mainClass}__nav-item`, {
                            [`${mainClass}__nav-item--button`]: isDefault,
                            [`${mainClass}__nav-item--active`]: isActive,
                          })}
                          type="button"
                          {...(item?.href
                            ? {
                                to: item.href,
                                end: true,
                                onClick: () => {
                                  setIsOpen(false);
                                  setSubmenu(null);
                                  setCreateTaskOpen(false);
                                  setSendNotificationOpen(false);

                                  if (typeof item?.onClick === "function")
                                    item.onClick();
                                },
                              }
                            : { onClick: item?.onClick })}
                        >
                          {!isDefault && (
                            <i>
                              {item?.badge && <i>{item.badge}</i>}
                              {item?.icon}
                            </i>
                          )}
                          <span>{item.label}</span>
                          {item?.submenu && (
                            <span className={`${mainClass}__nav-item-arrow`}>
                              <ArrowIcon />
                            </span>
                          )}
                        </Tag>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
        <div className={`${mainClass}__dock`}>
          {dock.map((item, index) => (
            <div key={index} className={`${mainClass}__dock-item`}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.button
                  ref={(el) => (buttonRefs.current[index] = el)}
                  key={!!item?.replace && isOpen ? "replace" : "default"}
                  type="button"
                  onClick={() => {
                    setCreateTaskOpen(false);
                    setSendNotificationOpen(false);

                    if (item?.onClick || item?.replace?.onClick)
                      isOpen
                        ? (item?.replace ?? item).onClick()
                        : item?.onClick();

                    if (item?.href || item?.replace?.href) {
                      navigate(
                        isOpen ? (item?.replace?.href ?? item.href) : item.href,
                      );

                      if (isOpen) setIsOpen(false);
                    }
                  }}
                  style={
                    buttonMinWidths[index]
                      ? { minWidth: buttonMinWidths[index] }
                      : undefined
                  }
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: "tween", duration: 0.2 }}
                >
                  <i>
                    {!isOpen && item?.badge && <i>{item.badge}</i>}
                    <AnimatePresence initial={false}>
                      <motion.span
                        key={!!item?.change && isOpen ? "change" : "default"}
                        initial={{ y: -8, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 8, opacity: 0 }}
                        transition={{ type: "tween", duration: 0.2 }}
                      >
                        {(item?.replace || item?.change) && isOpen
                          ? (item.replace?.icon ?? item.change?.icon)
                          : item.icon}
                      </motion.span>
                    </AnimatePresence>
                  </i>
                  <span>
                    <AnimatePresence initial={false}>
                      <motion.span
                        key={!!item?.change && isOpen ? "change" : "default"}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "tween", duration: 0.2 }}
                      >
                        {(item?.replace || item?.change) && isOpen
                          ? (item.replace?.label ?? item.change?.label)
                          : item.label}
                      </motion.span>
                    </AnimatePresence>
                  </span>
                </motion.button>
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      {user?.type !== "client" && (
        <>
          <TimeTracker />
          <SendNotification rmButton />
        </>
      )}

      {user?.type === "admin" && (
        <CreateTask
          isOpen={create_task_open}
          onClose={() => setCreateTaskOpen(false)}
        />
      )}
    </>
  );
};

export default MenuMobile;
